import { Directive, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges } from '@angular/core';

@Directive({
    selector: '[builderZoom]',
    standalone: true
})
export class BuilderZoomDirective implements OnChanges {
    @Input()
    builderZoomOff?: boolean;

    @Input()
    zoomScale = 1;

    @Input()
    zoomForcePositionTop?: number;

    @Input()
    zoomForcePositionLeft?: number;

    @Output()
    zoomMoveChange = new EventEmitter();

    @Output()
    zoomScaleChange = new EventEmitter<number>();

    private isDown: boolean = false;
    private posX = 0;
    private posY = 0;

    private startZoom: { x?: number; y?: number; distance?: number; originalX?: number; originalY?: number } = {};
    private startMove: { x?: number; y?: number; height?: number; width?: number } = {};
    private duringTouchZoom: boolean = false;

    /**
     * Wheel for zoom not needed now
     */
    // @HostListener('mousewheel', ['$event'])
    // protected onWheel(event: WheelEvent) {
    //     // zoom
    //     event.preventDefault(); // Prevent page scroll

    //     const scale = this.zoomScale + event.deltaY / -360;
    //     this.zoomScale = Math.min(Math.max(1, scale), 3);
    //     this.setTransform();
    // }

    /**
     * Mouse for move
     */
    @HostListener('mousedown', ['$event'])
    protected onMouseDown(event: MouseEvent) {
        this.startMove.height = this.element.offsetHeight;
        this.startMove.width = this.element.offsetWidth;
        this.zoomContainer.style.transition = '';
        if (!this.builderZoomOff) {
            this.isDown = true;
            this.element.style.cursor = 'grabbing';
            this.startMove.y = event.pageY;
            this.startMove.x = event.pageX;
        }
    }

    @HostListener('mouseup', ['$event'])
    protected onMouseUp(event: MouseEvent) {
        this.resetCursor();
    }

    @HostListener('mouseleave', ['$event'])
    protected onMouseLeave(event: MouseEvent) {
        this.resetCursor();
    }

    @HostListener('mousemove', ['$event'])
    protected onMouseMove(event: MouseEvent) {
        if (this.isDown) {
            event.preventDefault();

            const x = this.startMove.x ? this.startMove.x - event.pageX : 0;
            this.posX = this.getNewPostion(this.posX - x);
            const y = this.startMove.y ? this.startMove.y - event.pageY : 0;
            this.posY = this.getNewPostion(this.posY - y);

            this.setTransform();
            this.startMove.x = event.pageX;
            this.startMove.y = event.pageY;
        }
    }

    /**
     * Touch for zoom and move
     */
    @HostListener('touchstart', ['$event'])
    protected onTouchStart(event: TouchEvent) {
        this.startMove.height = this.element.offsetHeight;
        this.startMove.width = this.element.offsetWidth;
        this.zoomContainer.style.transition = '';
        if (event.touches.length === 2) {
            // zoom
            event.preventDefault();
            // Calculate where the fingers have started on the X and Y axis
            this.startZoom.x = (event.touches[0].pageX + event.touches[1].pageX) / 2;
            this.startZoom.y = (event.touches[0].pageY + event.touches[1].pageY) / 2;
            this.startZoom.originalX = this.posX;
            this.startZoom.originalY = this.posY;
            this.startZoom.distance = this.distance(event);
        } else if (event.touches.length === 1) {
            // move
            event.preventDefault();
            this.startMove.x = event.touches[0].pageX;
            this.startMove.y = event.touches[0].pageY;
        }
    }

    @HostListener('touchmove', ['$event'])
    protected onTouchMove(event: any | TouchEvent) {
        if (event.touches.length === 2) {
            this.duringTouchZoom = true;
            // zoom
            event.preventDefault(); // Prevent page scroll

            const deltaDistance = this.distance(event);
            const distance = this.startZoom.distance || 0;
            const isScaleDown = deltaDistance < distance;
            const scale = isScaleDown
                ? this.zoomScale - deltaDistance / distance / 8
                : this.zoomScale + (deltaDistance / distance - 1) / 6;

            if ((isScaleDown && scale < this.zoomScale) || (!isScaleDown && scale > this.zoomScale)) {
                this.zoomScale = Math.min(Math.max(1, scale), 3);

                // Calculate how much the fingers have moved on the X and Y axis

                const originalX = this.startZoom.originalX || 0;
                const originalY = this.startZoom.originalY || 0;

                this.posX = this.getNewPostion(
                    originalX +
                        ((event.touches[0].pageX + event.touches[1].pageX) / 2 - (this.startZoom.x || 0)) * 2 * this.zoomScale
                );
                this.posY = this.getNewPostion(
                    originalY +
                        ((event.touches[0].pageY + event.touches[1].pageY) / 2 - (this.startZoom.y || 0)) * 2 * this.zoomScale
                );

                this.setTransform();
            }
        } else if (event.touches.length === 1 && this.zoomScale > 1 && !this.duringTouchZoom) {
            // move
            const x = this.startMove.x ? this.startMove.x - event.touches[0].pageX : 0;
            this.posX = this.getNewPostion(this.posX - x);
            const y = this.startMove.y ? this.startMove.y - event.touches[0].pageY : 0;
            this.posY = this.getNewPostion(this.posY - y);

            this.setTransform();
            this.startMove.x = event.pageX;
            this.startMove.y = event.pageY;
        }
    }

    @HostListener('touchend', ['$event'])
    protected onTouchEnd(event: TouchEvent) {
        // check if user stops touching screen
        this.duringTouchZoom = event.touches.length !== 0;
        this.zoomScaleChange.emit(this.zoomScale);
        if (this.zoomScale === 1) {
            this.zoomContainer.style.transform = '';
            this.zoomContainer.style.webkitTransform = '';
        }
    }

    constructor(protected elementRef: ElementRef) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['builderZoomOff']) {
            this.resetCursor();
        }
        if (
            (changes['zoomForcePositionLeft'] || changes['zoomForcePositionTop']) &&
            (this.zoomForcePositionLeft !== undefined || this.zoomForcePositionTop !== undefined)
        ) {
            // -50 cause it % and 0 is center of image
            this.posX = (((this.zoomForcePositionLeft || 0) - 50) * this.zoomScale * this.element.offsetWidth) / -100;
            this.posY = (((this.zoomForcePositionTop || 0) - 50) * this.zoomScale * this.element.offsetHeight) / -100;
            this.zoomUpdate(false);
        } else if (changes['zoomScale']) {
            this.zoomUpdate();
        }
    }

    private zoomUpdate(emitChange = true) {
        this.zoomContainer.style.transition = 'transform 0.5s ease-in-out';
        if (this.zoomScale === 1) {
            this.zoomContainer.style.transform = '';
            this.zoomContainer.style.webkitTransform = '';
            this.posX = 0;
            this.posY = 0;
        } else {
            this.posX = this.getNewPostion(this.posX);
            this.posY = this.getNewPostion(this.posY);
            this.setTransform(emitChange);
        }
    }

    private distance(event: TouchEvent) {
        return Math.hypot(event.touches[0].pageX - event.touches[1].pageX, event.touches[0].pageY - event.touches[1].pageY);
    }

    private setTransform(emitChange = true): void {
        if (emitChange) {
            this.zoomMoveChange.emit();
        }

        const transform = `translate(${this.posX}px, ${this.posY}px) scale(${this.zoomScale})`;
        this.zoomContainer.style.transform = transform;
        this.zoomContainer.style.webkitTransform = transform;
    }

    private getNewPostion(newValue: number) {
        const currentSize = Math.min(this.element.offsetHeight, this.element.offsetWidth);
        const maxMinSize = ((this.zoomScale - 1) / 2) * currentSize;
        return Math.max(Math.min(maxMinSize, newValue), -1 * maxMinSize);
    }

    private resetCursor() {
        this.isDown = false;
        if (this.builderZoomOff) {
            this.element.style.cursor = 'default';
        } else {
            this.element.style.cursor = 'grab';
        }
    }

    get element() {
        return this.elementRef.nativeElement as HTMLElement;
    }

    get zoomContainer() {
        return this.element.firstChild as HTMLElement;
    }
}
